import { observer } from 'mobx-react-lite';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useEffect } from 'react';

import { Hero } from '@cfm-media/pwa-ui-components';
import { LoadableStatus } from '../mobx-utils';
import { parseHtml } from '../core/utils';
import { useStore } from '../core/store';
import { useTranslation } from '../core/utils/translations';

import { ContentWrapper } from '../components/content-wrapper';
import { ErrorPage } from '../components/error-page';
import { Loader } from '../components/loader';
import { PageWrapper } from '../components/page-wrapper';
import { urls } from '../urls';
import { useBookingDate } from '../hooks/useBookingDate';

export const Contact = observer(() => {
  const { t } = useTranslation('contact-page');

  const { booking, contacts } = {
    ...useStore().BookingStore,
    ...useStore().ContactStore,
  };

  const { isActiveBooking } = useBookingDate(-48, 48);

  useEffect(() => {
    if (!contacts.data) contacts.fetch();
  }, []);

  // prettier-ignore
  if ([booking?.status, contacts?.status].some((status) => status === LoadableStatus.error)) {
    return <ContactError />;
  } else if ([booking?.status, contacts?.status].some((status) => status !== LoadableStatus.done)) {
    return <Loader />;
  }

  return (
    <PageWrapper>
      <Hero
        image="https://images.unsplash.com/photo-1488998427799-e3362cec87c3?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2000&q=80"
        iconClass="icon-contact"
        headline="Kontakt"
        overlay
      />

      <ContentWrapper>
        <div className="sm:grid sm:grid-cols-2">
          <TravelAgencyInfo data={contacts?.data?.toJSON().travelAgency} />

          {isActiveBooking ? (
            <IncomingAgencyInfo
              data={contacts?.data?.toJSON().incomingAgency}
            />
          ) : (
            <div className="mt-4">
              <h2 className="text-2xl text-brand-1 font-bold mb-4">
                {t('contact-page-during-travel-text')}
              </h2>

              <div className="mt-4">{t('contact-no-information-yet-text')}</div>
            </div>
          )}
        </div>
      </ContentWrapper>
    </PageWrapper>
  );
});

// TODO: styles
export const IncomingAgencyInfo = ({ data }) => {
  const { t } = useTranslation('contact-page');

  return (
    <div className="mt-4">
      <h2 className="text-2xl text-brand-1 font-bold mb-4">
        {t('contact-page-during-travel-text')}
      </h2>

      <div className="mb-4">
        <div className="font-bold mb-2">{t('contact-partner-text')}</div>
        <div>{parseHtml(data?.contact?.split('\n').join('<br />'))}</div>
      </div>
    </div>
  );
};

// TODO: styles
export const TravelAgencyInfo = ({ data }) => {
  const { t } = useTranslation('contact-page');

  const parsedPhoneNumber = data?.phone
    ? parsePhoneNumber(data?.phone, 'DE')
    : null;

  return (
    <div className="mt-4">
      <h2 className="text-2xl text-brand-1 font-bold mb-4">
        {t('contact-travel-agency-text')}
      </h2>

      <div className="mb-4">
        <div className="font-bold mb-1">{t('contact-partner-text')}</div>
        <div className="mb-4">{data?.name}</div>

        {parsedPhoneNumber ? (
          <div className="mb-4">
            <div className="font-bold mb-1">{t('contact-telephone-text')}</div>
            <a
              className="underline hover:no-underline"
              href={parsedPhoneNumber.getURI()}
            >
              {parsedPhoneNumber.formatInternational()}
            </a>
          </div>
        ) : null}

        <div className="font-bold mb-1">{t('contact-email-text')}</div>

        <a
          className="underline hover:no-underline"
          href={`mailto:${data?.email}`}
        >
          {data?.email}
        </a>
      </div>
    </div>
  );
};

export const ContactError = () => {
  const { t } = useTranslation('error-pages');

  return (
    <PageWrapper>
      <Hero
        image="https://images.unsplash.com/photo-1488998427799-e3362cec87c3?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2000&q=80"
        iconClass="icon-contact"
        headline="Kontakt"
        overlay
      />
      <ContentWrapper>
        <ErrorPage
          title={t('contact-empty-error-title-text')}
          body={t('contact-empty-error-body-text')}
          link={urls.home}
          buttonText={t('contact-empty-error-button-text')}
        />
      </ContentWrapper>
    </PageWrapper>
  );
};
